import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with auth interceptor
const apiClient = axios.create({ baseURL: API });

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("auth_user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  login: (email, password, device_token) => axios.post(`${API}/auth/login`, { email, password, device_token }),
  verifyOtp: (email, otp_code, remember_device) => axios.post(`${API}/auth/verify-otp`, { email, otp_code, remember_device }),
  forgotPassword: (email) => axios.post(`${API}/auth/forgot-password`, { email }),
  getAuthConfig: () => axios.get(`${API}/auth/config`),
  resetPassword: (token, new_password) => axios.post(`${API}/auth/reset-password`, { token, new_password }),
  getMe: () => apiClient.get("/auth/me"),
  // Users
  createUser: (data) => apiClient.post("/auth/users", data),
  listUsers: () => apiClient.get("/auth/users"),
  deleteUser: (userId) => apiClient.delete(`/auth/users/${userId}`),
  updateUserPassword: (userId, data) => apiClient.put(`/auth/users/${userId}/password`, data),
  updateUserEmail: (userId, data) => apiClient.put(`/auth/users/${userId}/email`, data),

  // Dashboard
  getDashboardStats: () => apiClient.get("/dashboard/stats"),
  getGeoStats: () => apiClient.get("/dashboard/geo-stats"),

  // Settings
  getSettings: () => apiClient.get("/settings"),
  updateSettings: (data) => apiClient.put("/settings", data),
  getErrorLogs: () => apiClient.get("/settings/logs"),
  clearErrorLogs: () => apiClient.delete("/settings/logs"),

  // Cases
  createCase: (data) => apiClient.post("/cases", data),
  deleteCase: (caseId) => apiClient.delete(`/cases/${caseId}`),
  getCases: (search = "") => apiClient.get("/cases", { params: { search } }),
  getCase: (caseId) => apiClient.get(`/cases/${caseId}`),
  updateCaseDetails: (caseId, data) => apiClient.put(`/cases/${caseId}/details`, data),

  // Consent
  recordConsent: (caseId, data) => apiClient.post(`/cases/${caseId}/consent`, data),
  getConsent: (caseId) => apiClient.get(`/cases/${caseId}/consent`),
  generateConsentForm: (caseId, data) => apiClient.post(`/cases/${caseId}/consent-form`, data, { responseType: 'blob' }),
  downloadConsent: (caseId) => apiClient.get(`/cases/${caseId}/consent/pdf`, { responseType: 'blob' }).then(res => {
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `Consent_${caseId}.pdf`); // Ideally get filename from header
    document.body.appendChild(link);
    link.click();
    link.remove();
  }),

  // Audio
  uploadAudio: (caseId, formData) => {
    // formData should already contain file and recording_name
    return apiClient.post(`/cases/${caseId}/audio`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  getRecordings: (caseId) => apiClient.get(`/cases/${caseId}/recordings`),
  deleteAudio: (caseId, audioId) => apiClient.delete(`/cases/${caseId}/audio/${audioId}`),

  // Transcription
  transcribe: (caseId, data) => {
    if (data && data.audio_ids) {
      // Multiple audio files
      return apiClient.post(`/cases/${caseId}/transcribe`, { audio_ids: data.audio_ids });
    } else if (data && data.audioId) {
      // Single audio file (backward compat)
      return apiClient.post(`/cases/${caseId}/transcribe`, null, { params: { audio_id: data.audioId } });
    } else {
      // Default (latest)
      return apiClient.post(`/cases/${caseId}/transcribe`);
    }
  },
  getTranscript: (caseId) => apiClient.get(`/cases/${caseId}/transcript`),
  updateTranscript: (caseId, transcriptText) => apiClient.put(`/cases/${caseId}/transcript`, { transcript_text: transcriptText }),
  createManualTranscript: (caseId, transcriptText) => apiClient.post(`/cases/${caseId}/transcript/manual`, { transcript_text: transcriptText }),

  // Analysis
  analyze: (caseId, data) => apiClient.post(`/cases/${caseId}/analyze`, data),
  getScores: (caseId) => apiClient.get(`/cases/${caseId}/scores`),
  updateScore: (caseId, domainKey, data) =>
    apiClient.put(`/cases/${caseId}/scores/${domainKey}`, data),

  // Report
  generateReport: (caseId) => apiClient.post(`/cases/${caseId}/report`),
  getReport: (caseId) => apiClient.get(`/cases/${caseId}/report`),
  updateReport: (caseId, data) => apiClient.put(`/cases/${caseId}/report`, data),
  revertReport: (caseId) => apiClient.post(`/cases/${caseId}/report/revert`),

  // Export
  exportJson: (caseId) => apiClient.get(`/cases/${caseId}/export/json`),
  exportPdf: (caseId) => {
    const token = localStorage.getItem("auth_token");
    return `${API}/cases/${caseId}/export/pdf?token=${token}`;
  },

  submitCase: (caseId) => apiClient.post(`/cases/${caseId}/submit`, {}),

  // Client history
  getClientHistory: (clientId) => apiClient.get(`/clients/${encodeURIComponent(clientId)}/history`),



  // Matrix
  getMatrix: () => axios.get(`${API}/matrix`),

  // Audits
  trackActivity: (data) => apiClient.post("/auth/activity/track", data),
  getAuditLogs: (password) => apiClient.get("/admin/logs", { headers: { "X-Admin-Access": password } }),
};

// Auth helpers
export const auth = {
  isLoggedIn: () => !!localStorage.getItem("auth_token"),
  getUser: () => {
    const user = localStorage.getItem("auth_user");
    return user ? JSON.parse(user) : null;
  },
  setAuth: (token, user) => {
    localStorage.setItem("auth_token", token);
    localStorage.setItem("auth_user", JSON.stringify(user));
  },
  logout: () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    window.location.href = "/login";
  },
  isSuperAdmin: () => {
    const user = auth.getUser();
    return user?.role === "super_admin";
  },
  isAdmin: () => {
    const user = auth.getUser();
    return user?.role === "super_admin" || user?.role === "admin";
  },
};

export default api;
